import React, { useState } from 'react';
import { Button, Modal, Space, Table, Tag, Tooltip, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { showError } from 'utils/exceptionUtils';
import {
  DELETE_DATA_RETENTION_POLICY,
  DataRetentionPolicyItem,
  LIST_DATA_RETENTION_POLICIES,
  ListDataRetentionPoliciesResponse,
  PREVIEW_DATA_RETENTION_CLEANUP,
  PreviewDataRetentionCleanupResponse,
  SET_DATA_RETENTION_POLICY_ACTIVE,
  TRIGGER_DATA_RETENTION_CLEANUP,
} from './graphql/dataRetentionQueries';
import PolicyDrawer from './components/PolicyDrawer';
import ExecutionHistoryModal from './components/ExecutionHistoryModal';
import { renderPhysicalDeleteWarning } from './utils/physicalDeleteWarning';

/**
 * 数据保留清理配置页。
 *
 * 管理各表的保留天数与开关；启用后由定时任务按保留期**物理删除**超期数据（不可恢复）。
 * 目标表来自后端白名单枚举，界面只能选不能填。
 */
const DataRetentionPage: React.FC = () => {
  const client = useApolloClient();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<DataRetentionPolicyItem | null>(null);
  const [historyPolicy, setHistoryPolicy] = useState<DataRetentionPolicyItem | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [previewResults, setPreviewResults] = useState<Record<string, string>>({});

  const { data, loading, refetch } = useQuery<ListDataRetentionPoliciesResponse>(
    LIST_DATA_RETENTION_POLICIES,
    { fetchPolicy: 'network-only' },
  );

  const [setActive] = useMutation(SET_DATA_RETENTION_POLICY_ACTIVE, {
    onCompleted: () => {
      message.success('操作成功');
      refetch();
    },
    onError: showError,
  });

  const [deletePolicy] = useMutation(DELETE_DATA_RETENTION_POLICY, {
    onCompleted: () => {
      message.success('删除成功');
      refetch();
    },
    onError: showError,
  });

  const [triggerCleanup] = useMutation(TRIGGER_DATA_RETENTION_CLEANUP, {
    onCompleted: () => message.success('已提交执行，可在「执行历史」中查看进度'),
    onError: showError,
  });

  const policies = data?.listDataRetentionPolicies ?? [];

  const handleToggleActive = (record: DataRetentionPolicyItem) => {
    if (record.isActive) {
      // 停用是安全动作（急停），不需要吓人的确认
      Modal.confirm({
        title: '停用清理策略',
        content: `停用后将不再清理 ${record.tableName} 的历史数据。`,
        okText: '确认停用',
        cancelText: '取消',
        onOk: () => setActive({ variables: { id: record.id, isActive: false } }),
      });
      return;
    }
    Modal.confirm({
      title: '启用清理策略',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: '我确认，启用',
      cancelText: '取消',
      width: 560,
      content: renderPhysicalDeleteWarning(record.tableName, record.effectiveRetentionDays),
      onOk: () => setActive({ variables: { id: record.id, isActive: true } }),
    });
  };

  const handleDelete = (record: DataRetentionPolicyItem) => {
    if (record.isActive) {
      // 与后端 DataRetentionPolicyServiceImpl.deletePolicy 的规则一致，前端先拦一道
      Modal.warning({
        title: '无法删除',
        content: `策略「${record.tableName}」处于启用状态，请先停用后再删除。`,
      });
      return;
    }
    Modal.confirm({
      title: '删除清理策略',
      content: `确认删除 ${record.tableName} 的保留策略？删除后该表将不再被自动清理。`,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: () => deletePolicy({ variables: { id: record.id } }),
    });
  };

  const handleTrigger = (record: DataRetentionPolicyItem) => {
    Modal.confirm({
      title: '立即执行一次清理',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: '确认执行',
      cancelText: '取消',
      width: 560,
      content: renderPhysicalDeleteWarning(record.tableName, record.effectiveRetentionDays),
      onOk: () => triggerCleanup({ variables: { id: record.id } }),
    });
  };

  /** 按需预览：大表 COUNT 有开销，不随列表自动加载 */
  const handlePreview = async (record: DataRetentionPolicyItem) => {
    setPreviewing(record.id);
    try {
      const { data: previewData } = await client.query<PreviewDataRetentionCleanupResponse>({
        query: PREVIEW_DATA_RETENTION_CLEANUP,
        variables: { id: record.id },
        fetchPolicy: 'network-only',
      });
      const preview = previewData.previewDataRetentionCleanup;
      setPreviewResults((prev) => ({
        ...prev,
        [record.id]: preview.capped
          ? `${preview.pendingRows.toLocaleString()}+`
          : preview.pendingRows.toLocaleString(),
      }));
    } catch (e) {
      // 预览是只读 SELECT，受 max_execution_time 限制；大表超时属预期，降级为「未知」而非报错
      setPreviewResults((prev) => ({ ...prev, [record.id]: '未知（统计超时）' }));
    } finally {
      setPreviewing(null);
    }
  };

  const columns: TableColumnsType<DataRetentionPolicyItem> = [
    {
      title: '目标表',
      dataIndex: 'tableName',
      width: 220,
      render: (tableName: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{tableName}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.description}</div>
        </div>
      ),
    },
    {
      title: '时间列',
      dataIndex: 'timeColumn',
      width: 130,
      render: (v: string) => <code>{v}</code>,
    },
    {
      title: '保留天数',
      dataIndex: 'retentionDays',
      width: 150,
      render: (days: number, record) => (
        <div>
          <span>{days} 天</span>
          {record.effectiveRetentionDays !== days && (
            <Tooltip title={`低于该表下限 ${record.minRetentionDays} 天，实际按下限执行`}>
              <Tag color="warning" style={{ marginLeft: 6 }}>
                实际 {record.effectiveRetentionDays} 天
              </Tag>
            </Tooltip>
          )}
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>下限 {record.minRetentionDays} 天</div>
        </div>
      ),
    },
    {
      title: '批次参数',
      width: 170,
      render: (_, record) => (
        <div style={{ fontSize: 12, color: '#595959' }}>
          <div>单批 {record.batchSize} 行 / 轮上限 {record.maxBatchesPerRun} 批</div>
          <div>批间延时 {record.batchDelayMs}ms</div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>{isActive ? '清理中' : '已停用'}</Tag>
      ),
    },
    {
      title: '待清理量',
      width: 170,
      render: (_, record) => (
        <Space>
          <span>{previewResults[record.id] ?? '-'}</span>
          <Button
            type="link"
            size="small"
            loading={previewing === record.id}
            onClick={() => handlePreview(record)}
          >
            查询
          </Button>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 260,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingPolicy(record);
              setDrawerVisible(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" size="small" onClick={() => handleToggleActive(record)}>
            {record.isActive ? '停用' : '启用'}
          </Button>
          <Button
            type="link"
            size="small"
            disabled={!record.isActive}
            onClick={() => handleTrigger(record)}
          >
            立即执行
          </Button>
          <Button type="link" size="small" onClick={() => setHistoryPolicy(record)}>
            执行历史
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>数据保留清理</h2>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            按保留期物理删除历史数据。删除不可恢复，且不会归还磁盘空间（空间留在表内复用）。
          </div>
        </div>
        <Button
          type="primary"
          onClick={() => {
            setEditingPolicy(null);
            setDrawerVisible(true);
          }}
        >
          新建策略
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={policies}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
      />

      <PolicyDrawer
        visible={drawerVisible}
        policy={editingPolicy}
        existingTargets={policies.map((p) => p.target)}
        onClose={() => setDrawerVisible(false)}
        onSuccess={() => {
          setDrawerVisible(false);
          refetch();
        }}
      />

      <ExecutionHistoryModal policy={historyPolicy} onClose={() => setHistoryPolicy(null)} />
    </div>
  );
};

export default DataRetentionPage;
